import { ProfileNavbarInterface } from '@type/header-types';
import { useTranslation } from 'next-i18next';
import { dataTestId } from '@services/data-test-id';
import { Tooltip } from '@components/common';
import { NeuroIcon } from '@components/common/Icons';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useState } from 'react';
import { composeProfileTextClasses } from './_profileNavbarStyles';
import { ProfileAvatar } from './ProfileAvatar';
/**
 * @description Renders the profile with image, name, email and score. Used in the navbar or
 * somewhere else if needed.
 * * **headerInfo** The header info data
 * * **size** The size of the profile image
 * * **className** Extra classes
 */
export const ProfileInNavbar = (props: ProfileNavbarInterface) => {
  // Component props
  const { headerInfo, size = 'sm', className } = props;
  // translate
  const { t } = useTranslation('common');
  // router
  const { locale } = useExtendedRouter();
  // Fetch negative consumption limit
  const [hasOpenedTooltip, setHasOpenedTooltip] = useState(false);
  // Classes
  // ******************************************
  const nameClasses = composeProfileTextClasses(size, 'name');
  // Data aliases
  // ******************************************
  const isCompany = headerInfo?.hasBusinessWallet;
  const name = isCompany ? headerInfo?.name || t('account.label.myaccount') : headerInfo?.candidateName || t('account.label.myaccount');
  const headerInfoExtended = headerInfo as ApiSchema<'HeaderInfo'> & { discountPercentage?: number };
  const discount = headerInfoExtended?.discountPercentage ?? 0;
  const totalCredits = headerInfo?.ordersNotificationData?.totalCredits ?? 0;
  // Render component
  // Short-circuit if the headerInfo is null
  // ******************************************
  if (!headerInfo) return (<></>);
  return (
    <span
      className={`flex items-center gap-3 ${className || ''}`}
      {...dataTestId('navbar.profile')}
    >
      {/* Avatar */}
      <ProfileAvatar headerInfo={headerInfo} className="md:hidden" />
      {/* User info; accounts created with the phone number don't have an email address initially */}
      <span className="hidden flex-col items-start lg:flex">
        <span className={nameClasses}>{name}</span>
        <Tooltip
          displayStyle="block"
          onMouseEnter={() => setHasOpenedTooltip(true)}
          tooltipText={(
            <div className="flex flex-col gap-1.5 p-2 text-left text-xs">
              <div className="flex items-start justify-between gap-4">
                <span className="text-ink-medium">{t('navbar.credits.tooltip-balance')}</span>
                <span className="font-bold">
                  {headerInfo?.walletCredits}
                </span>
              </div>
              {totalCredits > 0 && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-ink-medium">
                    {t('navbar.topup_initiated')}
                  </span>
                  <span className="font-bold">
                    {totalCredits}
                  </span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex items-start justify-between gap-4">
                  <span className="text-ink-medium">{t('consultant.info.box.basic.title')}</span>
                  <span className="font-bold text-secondary">
                    {discount}
                    %
                  </span>
                </div>
              )}
            </div>
          )}
          position="bottom"
          floating
          useFloatingPortal
        >
          <div className="flex items-baseline gap-0.5 font-bold">
            <div className="flex items-baseline gap-0.5">
              {`${headerInfo?.walletCredits}`}
              <NeuroIcon className="size-1.75" />
            </div>
            {totalCredits > 0 && (
              <span className="px-1 font-thin text-ink whitespace-nowrap">
                ({totalCredits} {t('navbar.topup_pending')})
              </span>
            )}
            {discount > 0 && totalCredits == 0 && (
              <span className="ml-1 rounded-md bg-secondary/20 px-1 py-1 text-pico font-thin text-secondary">
                {discount}
                % OFF
              </span>
            )}
          </div>
        </Tooltip>
      </span>
    </span>
  );
};
